/**
 * F39 (Wave 2C) — admin active-placements monitor.
 *
 * Route: `/placements`
 * Permission: `placement_inventory:read` (super_admin + support + finance +
 *             content_moderator) — view.
 *             Override-cancel gated additionally on `placement_approvals:approve`;
 *             manual credit-grant gated additionally on `placement_credits:grant`.
 *             The server re-enforces.
 *
 * Cross-venue list with status / type / venue `<Select>` filters (URL-param
 * driven). Row actions: open the detail page, override-cancel (Dialog), or
 * grant goodwill credit to the venue (Dialog).
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useI18n } from '@bravemove/i18n/react';
import {
  Banner,
  Button,
  CurrencyDisplay,
  EmptyState,
  Pagination,
  PlacementStateBadge,
  Select,
  Skeleton,
  StatusChip,
  Table,
  type TableColumn,
  useToast,
} from '@bravemove/ui-web';
import { PLACEMENT_STATUSES, PLACEMENT_TYPES, type PlacementStatus } from '@bravemove/types';
import {
  adminPlacementsApi,
  type AdminPlacementRow,
  type ListPlacementsOpts,
} from '../../api/placements';
import { translateApiError } from '../../auth/errors';
import { useAuth } from '../../auth/useAuth';
import { PageHeader } from '../../components/PageHeader';
import { CancelPlacementDialog } from '../../components/placements/CancelPlacementDialog';
import { GrantCreditDialog } from '../../components/placements/GrantCreditDialog';
import { PromoteTournamentDialog } from '../../components/placements/PromoteTournamentDialog';
import { isPlacementBadgeState } from './placementBadge';

const PAGE_SIZE = 25;

const CANCELLABLE = new Set<PlacementStatus>(['pending_approval', 'scheduled', 'active']);

export function PlacementMonitorPage() {
  const { t, locale } = useI18n();
  const navigate = useNavigate();
  const toast = useToast();
  const auth = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();

  const canCancel = auth.adminUser?.permissions?.includes('placement_approvals:approve') ?? false;
  const canGrant = auth.adminUser?.permissions?.includes('placement_credits:grant') ?? false;
  // rev-10 follow-up — the free admin platform-promote of a sponsored tournament
  // shares the approve permission (`placement_approvals:approve`).
  const canPromote = canCancel;

  const statusParam = searchParams.get('status') ?? 'all';
  const typeParam = searchParams.get('type') ?? 'all';
  const venueParam = searchParams.get('venueId') ?? '';
  const pageParam = Number(searchParams.get('page') ?? '1') || 1;

  const [rows, setRows] = useState<readonly AdminPlacementRow[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [cancelTarget, setCancelTarget] = useState<AdminPlacementRow | null>(null);
  const [grantTarget, setGrantTarget] = useState<AdminPlacementRow | null>(null);
  const [promoteOpen, setPromoteOpen] = useState(false);

  const fetchList = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const opts: ListPlacementsOpts = {
        page: pageParam,
        perPage: PAGE_SIZE,
        ...(statusParam !== 'all' ? { status: statusParam as PlacementStatus } : {}),
        ...(typeParam !== 'all' ? { type: typeParam as (typeof PLACEMENT_TYPES)[number] } : {}),
        ...(venueParam ? { venueId: venueParam } : {}),
      };
      const data = await adminPlacementsApi.listPlacements(opts);
      setRows(data.data);
      setTotal(data.pagination.total);
    } catch (err) {
      setError(translateApiError(err, t));
    } finally {
      setLoading(false);
    }
  }, [statusParam, typeParam, venueParam, pageParam, t]);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    void fetchList();
  }, [fetchList]);

  const setParam = (key: string, value: string) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (value && value !== 'all') next.set(key, value);
      else next.delete(key);
      next.delete('page');
      return next;
    });
  };

  const onPageChange = (next: number) => {
    setSearchParams((prev) => {
      const np = new URLSearchParams(prev);
      np.set('page', String(next));
      return np;
    });
  };

  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const onCancel = async (reason: string, grantCreditMinor?: number) => {
    if (!cancelTarget) return;
    await adminPlacementsApi.cancelPlacement(cancelTarget.id, {
      reason,
      ...(grantCreditMinor !== undefined ? { grantCreditMinor } : {}),
    });
    toast.push({
      tone: 'success',
      title: t('placement:admin.monitor.toast_cancelled', { defaultValue: 'Placement cancelled.' }),
    });
    void fetchList();
  };

  const onGrant = async (deltaMinor: number, reason: string) => {
    if (!grantTarget || !grantTarget.venueId) return;
    await adminPlacementsApi.grantCredit(grantTarget.venueId, { deltaMinor, reason });
    toast.push({
      tone: 'success',
      title: t('placement:admin.monitor.toast_granted', { defaultValue: 'Credit granted.' }),
    });
  };

  const columns = useMemo<readonly TableColumn<AdminPlacementRow>[]>(
    () => [
      {
        key: 'venue',
        header: t('placement:admin.monitor.col_venue', { defaultValue: 'Venue' }),
        cell: (row) => (
          <button
            type="button"
            className="flex flex-col gap-0.5 text-start hover:underline"
            onClick={() => navigate(`/placement-approvals/${row.id}`)}
            data-testid={`placement-monitor-link-${row.id}`}
          >
            <span className="text-[13px] font-semibold text-ink">
              {row.venueName ?? row.venueId}
            </span>
            <span className="text-[11px] text-ink-3">
              {t(`placement:type.${row.placementType}`, { defaultValue: row.placementType })}
            </span>
          </button>
        ),
      },
      {
        key: 'status',
        header: t('placement:admin.monitor.col_status', { defaultValue: 'Status' }),
        cell: (row) => (
          <div className="flex flex-col items-start gap-1">
            {isPlacementBadgeState(row.status) ? (
              <PlacementStateBadge
                size="sm"
                state={row.status}
                label={t(`placement:status.${row.status}`, { defaultValue: row.status })}
              />
            ) : (
              <StatusChip
                size="sm"
                status="rejected"
                label={t(`placement:status.${row.status}`, { defaultValue: row.status })}
              />
            )}
            {/* FIX 4.12 (D-SPN) — nothing delivers a sponsored notification; one
                bought before migration 0054 should be cancelled before it bills. */}
            {row.placementType === 'sponsored_notification' && CANCELLABLE.has(row.status) ? (
              <StatusChip
                size="sm"
                status="expiring"
                label={t('placement:admin.monitor.not_delivered', {
                  defaultValue: 'Not delivered: cancel it',
                })}
                data-testid={`placement-monitor-undelivered-${row.id}`}
              />
            ) : null}
          </div>
        ),
      },
      {
        key: 'perf',
        header: t('placement:admin.monitor.col_performance', { defaultValue: 'Performance' }),
        cell: (row) =>
          row.performance ? (
            <span className="text-[12px] text-ink-2">
              {t('placement:admin.monitor.perf_value', {
                impressions: row.performance.impressions,
                clicks: row.performance.clicks,
                conversions: row.performance.conversions,
                defaultValue: '{impressions} imp · {clicks} clk · {conversions} conv',
              })}
            </span>
          ) : (
            <span className="text-ink-3">—</span>
          ),
      },
      {
        key: 'charge',
        header: t('placement:admin.monitor.col_charge', { defaultValue: 'Net charge' }),
        align: 'end',
        cell: (row) => (
          <CurrencyDisplay
            amount={row.netChargeMinor}
            currency={row.priceCurrency}
            locale={locale}
          />
        ),
      },
      {
        key: 'actions',
        header: t('placement:admin.monitor.col_actions', { defaultValue: 'Actions' }),
        align: 'end',
        cell: (row) => (
          <div className="flex justify-end gap-2">
            {/* Credit is venue-scoped — a tournament-promoted placement has no
                venue, so the grant action is hidden (it could never succeed). */}
            {canGrant && row.venueId ? (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setGrantTarget(row)}
                data-testid={`placement-monitor-grant-${row.id}`}
              >
                {t('placement:admin.monitor.grant_credit', { defaultValue: 'Grant credit' })}
              </Button>
            ) : null}
            {canCancel && CANCELLABLE.has(row.status) ? (
              <Button
                variant="secondary"
                size="sm"
                onClick={() => setCancelTarget(row)}
                data-testid={`placement-monitor-cancel-${row.id}`}
              >
                {t('placement:admin.monitor.override_cancel', { defaultValue: 'Cancel' })}
              </Button>
            ) : null}
          </div>
        ),
      },
    ],
    [t, locale, navigate, canGrant, canCancel],
  );

  return (
    <div className="space-y-6" data-testid="placement-monitor-page">
      <div className="flex items-start justify-between gap-4">
        <PageHeader
          title={t('placement:admin.monitor.title', { defaultValue: 'Active placements' })}
          subtitle={t('placement:admin.monitor.subtitle', {
            defaultValue:
              'Cross-venue placement monitor. Override-cancel for issues; grant goodwill credit.',
          })}
        />
        {canPromote ? (
          <Button
            variant="primary"
            onClick={() => setPromoteOpen(true)}
            data-testid="placement-monitor-promote-cta"
          >
            {t('admin:placement.promoteDialog.cta', { defaultValue: 'Promote a tournament' })}
          </Button>
        ) : null}
      </div>

      <div className="flex flex-wrap items-end gap-3">
        <div className="min-w-[180px]">
          <label
            className="block text-[11px] uppercase tracking-wider text-ink-3 mb-1"
            htmlFor="placement-monitor-status-filter"
          >
            {t('placement:admin.monitor.filter_status', { defaultValue: 'Status' })}
          </label>
          <Select
            id="placement-monitor-status-filter"
            value={statusParam}
            data-testid="placement-monitor-status-filter"
            aria-label={t('placement:admin.monitor.filter_status', { defaultValue: 'Status' })}
            options={[
              {
                value: 'all',
                label: t('placement:admin.monitor.filter_all', { defaultValue: 'All' }),
              },
              ...PLACEMENT_STATUSES.map((s) => ({
                value: s,
                label: t(`placement:status.${s}`, { defaultValue: s }),
              })),
            ]}
            onChange={(e) => setParam('status', e.target.value)}
          />
        </div>
        <div className="min-w-[200px]">
          <label
            className="block text-[11px] uppercase tracking-wider text-ink-3 mb-1"
            htmlFor="placement-monitor-type-filter"
          >
            {t('placement:admin.monitor.filter_type', { defaultValue: 'Type' })}
          </label>
          <Select
            id="placement-monitor-type-filter"
            value={typeParam}
            data-testid="placement-monitor-type-filter"
            aria-label={t('placement:admin.monitor.filter_type', { defaultValue: 'Type' })}
            options={[
              {
                value: 'all',
                label: t('placement:admin.monitor.filter_all', { defaultValue: 'All' }),
              },
              ...PLACEMENT_TYPES.map((c) => ({
                value: c,
                label: t(`placement:type.${c}`, { defaultValue: c }),
              })),
            ]}
            onChange={(e) => setParam('type', e.target.value)}
          />
        </div>
      </div>

      {error ? <Banner tone="danger">{error}</Banner> : null}

      {loading ? (
        <div className="space-y-3" data-testid="placement-monitor-loading">
          {Array.from({ length: 6 }, (_, i) => (
            <Skeleton key={i} className="h-12 w-full rounded-lg" />
          ))}
        </div>
      ) : rows.length === 0 ? (
        <EmptyState
          title={t('placement:admin.monitor.empty', {
            defaultValue: 'No placements in this view.',
          })}
        />
      ) : (
        <>
          <Table
            data={rows as AdminPlacementRow[]}
            columns={columns as TableColumn<AdminPlacementRow>[]}
            rowKey={(row) => row.id}
          />
          {pageCount > 1 ? (
            <div className="flex justify-end">
              <Pagination
                page={pageParam}
                pageCount={pageCount}
                onChange={onPageChange}
                ariaLabel={t('placement:admin.monitor.title', {
                  defaultValue: 'Placements pagination',
                })}
              />
            </div>
          ) : null}
        </>
      )}

      {cancelTarget ? (
        <CancelPlacementDialog
          open={cancelTarget !== null}
          currency={cancelTarget.priceCurrency}
          onConfirm={onCancel}
          onClose={() => setCancelTarget(null)}
        />
      ) : null}

      {grantTarget ? (
        <GrantCreditDialog
          open={grantTarget !== null}
          venueLabel={grantTarget.venueName ?? grantTarget.venueId ?? '—'}
          currency={grantTarget.priceCurrency}
          onConfirm={onGrant}
          onClose={() => setGrantTarget(null)}
        />
      ) : null}

      {/* rev-10 follow-up — free admin platform-promote of a sponsored tournament. */}
      <PromoteTournamentDialog
        open={promoteOpen}
        onClose={() => setPromoteOpen(false)}
        onPromoted={() => {
          toast.push({
            tone: 'success',
            title: t('admin:placement.promoteDialog.done', {
              defaultValue: 'Tournament promoted.',
            }),
          });
          void fetchList();
        }}
      />
    </div>
  );
}

export default PlacementMonitorPage;
